// The World type, its lifecycle (createWorld), the fixed-step tick (step:
// advance aircraft, collisions, landings, go-arounds, the clear condition,
// events) and the two small player-facing commands that don't warrant their
// own file (toggleHold, flyOn). Tuned numbers live in tuning.ts, arrival
// scheduling/spawning in spawn.ts, path snapping in route.ts, read-only
// helpers in query.ts — this file wires them into the tick and owns nothing
// of theirs. Also re-exports the names bots/pilot.ts and bots/planner.ts
// deep-import from here directly (see COMPATIBILITY in the split's brief).
import type { Aircraft, Command, Pad, Runway, Scheduled, SimEvent, WorldState, WorldStats } from './types.ts';
import type { LevelDef, Layout } from './level.ts';
import { landingPoints, layoutLevel } from './level.ts';
import { makeRng, type Rng } from './rng.ts';
import { angleDiff, clamp } from './geometry.ts';
import { advanceAircraft } from './kinematics.ts';
import { LOGICAL_MIN, MAX_VIEW_SCALE, LINEUP_TOL } from './tuning.ts';
import { schedule, clearEntry, slideGate, spawnPlane, trafficCap } from './spawn.ts';
import { distToStrip } from './route.ts';
import { needsAttention, airborneCount } from './query.ts';

/** Fixed simulation step. Renderer and lab both advance the world in these.
 *  Structural (the tick rate), not a tuning knob, so it stays here rather
 *  than in tuning.ts. */
export const SIM_DT = 1 / 60;

export function viewScale(cssW: number, cssH: number): number {
  return clamp(Math.min(cssW, cssH) / LOGICAL_MIN, 1, MAX_VIEW_SCALE);
}

export interface World {
  W: number; H: number;
  level: LevelDef;
  layout: Layout;
  runways: Runway[];
  pads: Pad[];
  planes: Aircraft[];
  schedule: Scheduled[];
  /** arrivals safely down. Drives the traffic governor and the campaign's pace. */
  arrivals: number;
  /** the run's score: every arrival is worth its type times the city's
   *  difficulty (`landingPoints`), so what you land matters, not just how many */
  points: number;
  /** set once the baseline has been met and the player chose to fly on: the
   *  level no longer ends by itself, only a crash ends it (see `flyOn`) */
  endless: boolean;
  elapsed: number;
  lastSpawnAt: number;
  state: WorldState;
  rng: Rng;
  seed: number;
  nextId: number;
  /** events emitted during the most recent step(); cleared at the start of each step */
  events: SimEvent[];
  stats: WorldStats;
  /** pairs currently inside the warning band, keyed "a:b" (for near-miss counting) */
  warnPairs: Set<string>;
  /** advanced by one at the end of every step() (0 at creation) — the clock a
   *  Command's `tick` is measured against (see `log`, src/sim/replay.ts) */
  tick: number;
  /** when set (see `createWorld`'s `record` option), every state-changing
   *  command (`assignPath`, `toggleHold`, `flyOn`) appends itself here as it
   *  happens; undefined when recording is off, so an unrecorded run makes no
   *  extra allocation and behaves identically. */
  log?: Command[];
}

export function createWorld(level: LevelDef, W: number, H: number, seed: number, opts?: { record?: boolean }): World {
  const layout = layoutLevel(level, W, H);
  const rng = makeRng(seed);
  const w: World = {
    W, H, level, layout,
    runways: layout.runways, pads: layout.pads,
    planes: [], schedule: [], arrivals: 0, points: 0, endless: false, elapsed: 0, lastSpawnAt: -9,
    state: 'playing', rng, seed, nextId: 1, events: [],
    stats: { landed: 0, goArounds: 0, nearMisses: 0, crashes: 0, maxAirborne: 0, airborneTime: 0, warnTime: 0, idleTime: 0, gateHeldTime: 0, commands: 0 },
    warnPairs: new Set(),
    tick: 0,
    log: opts?.record ? [] : undefined,
  };
  // first aircraft appears immediately so the player has something to do —
  // the sky is empty, so its gate is clear by construction
  const first = schedule(w, 0);
  first.typeId = level.mix[0][0];
  spawnPlane(w, first, clearEntry(w, first)!);
  return w;
}

/**
 * Keep flying a city whose baseline is already met, for score alone. The
 * unlock is banked the moment the level clears, so this risks nothing but the
 * run's own points — and nothing ends it now except a crash.
 */
export function flyOn(w: World): void {
  if (w.state !== 'cleared') return;
  w.state = 'playing';
  w.endless = true;
  if (w.log) w.log.push({ tick: w.tick, t: 'flyOn' });
}

export function step(w: World, dt: number = SIM_DT): void {
  w.events.length = 0;
  if (w.state !== 'playing') { w.tick++; return; }
  w.elapsed += dt;

  // spawn governor: traffic is capped by how many are airborne — landing
  // quickly earns breathing room. Cap grows with arrivals, not with points:
  // what raises the pressure is how much of the level is behind you, and a
  // level whose mix pays better must not also fill the sky faster for it.
  const lv = w.level;
  const cap = trafficCap(w);
  while (airborneCount(w) + w.schedule.length < cap) {
    w.schedule.push(schedule(w, w.elapsed + lv.spawnLead[0] + w.rng.next() * (lv.spawnLead[1] - lv.spawnLead[0])));
    w.schedule.sort((a, b) => a.eta - b.eta);
  }
  const due = w.schedule[0];
  if (due && w.elapsed >= due.eta && w.elapsed - w.lastSpawnAt > lv.minSpawnGap) {
    // the gate rule: come through only into room the player can still work
    // with, otherwise wait outside and walk the edge until there is some
    const entry = clearEntry(w, due);
    if (entry) {
      spawnPlane(w, due, entry);
      w.schedule.shift();
      w.lastSpawnAt = w.elapsed;
    } else {
      slideGate(due, dt);
      w.stats.gateHeldTime += dt;
    }
  }

  for (const p of w.planes) {
    p.idleSince += dt;
    if (p.rollout > 0) { // landing: runway rollout, or hover-down on a pad
      p.rollout += dt;
      const dur = p.pad ? 1.5 : 2.6;
      const t = Math.min(p.rollout / dur, 1);
      p.speed = Math.max(0, p.speed * (1 - dt * (p.pad ? 3.2 : 0.9)));
      if (p.runway) p.ang += clamp(angleDiff(p.ang, p.runway.ang), -1.5 * dt, 1.5 * dt);
      p.x += Math.cos(p.ang) * p.speed * dt;
      p.y += Math.sin(p.ang) * p.speed * dt;
      p.fade = 1 - t;
      if (t >= 1) {
        const pts = landingPoints(p.type, w.level);
        p.gone = true; w.arrivals++; w.points += pts; w.stats.landed++;
        w.events.push({ t: 'landed', id: p.id, x: p.x, y: p.y, runwayId: p.runway?.id, padId: p.pad?.id, typeId: p.type.id, points: pts });
      }
      continue;
    }
    // waypoint following, turning, integration and soft wrap — shared with
    // bots/lab path prediction via advanceAircraft (src/sim/kinematics.ts)
    if (advanceAircraft(p, w, dt, (pp) => attemptTouchdown(w, pp))) {
      p.warn = Math.max(0, p.warn - dt * 2);
    }
  }
  w.planes = w.planes.filter(p => !p.gone);

  // proximity
  let anyWarn = false;
  const live = new Set<string>();
  for (let i = 0; i < w.planes.length; i++) {
    for (let j = i + 1; j < w.planes.length; j++) {
      const a = w.planes[i], b = w.planes[j];
      if (a.rollout > 0.6 || b.rollout > 0.6) continue;
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const crashD = 12 * (a.type.scale + b.type.scale);
      if (d < crashD) {
        w.stats.crashes++;
        w.events.push({ t: 'crash', a: a.id, b: b.id, x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
        w.state = 'dead';
        w.tick++;
        return;
      }
      if (d < crashD + 48) {
        a.warn = b.warn = 1; anyWarn = true;
        const key = `${a.id}:${b.id}`;
        live.add(key);
        if (!w.warnPairs.has(key)) { w.stats.nearMisses++; w.events.push({ t: 'nearmiss', a: a.id, b: b.id, d }); }
      }
    }
  }
  w.warnPairs = live;

  // stats
  const air = airborneCount(w);
  w.stats.maxAirborne = Math.max(w.stats.maxAirborne, air);
  w.stats.airborneTime += air * dt;
  if (anyWarn) w.stats.warnTime += dt;
  if (!w.planes.some(needsAttention)) w.stats.idleTime += dt;

  // the baseline is a score, not a tally of arrivals: land the heavy work and
  // a city clears sooner than it would on lights alone. Once the player has
  // chosen to fly on, nothing clears again — the level is already unlocked.
  if (!w.endless && w.points >= lv.baseline) {
    w.state = 'cleared';
    w.events.push({ t: 'cleared', points: w.points, arrivals: w.arrivals, elapsed: w.elapsed });
  }
  w.tick++;
}

function attemptTouchdown(w: World, p: Aircraft): void {
  if (p.pad) { // heli: hover down if actually over the pad
    if (Math.hypot(p.pad.x - p.x, p.pad.y - p.y) < 70) { p.rollout = 0.0001; p.fade = 1; }
    else { p.landing = false; p.pad = null; w.stats.goArounds++; w.events.push({ t: 'goaround', id: p.id, x: p.x, y: p.y }); }
    return;
  }
  const r = p.runway;
  // must be over the strip (anywhere along it, or 75px short of the bars on
  // the approach) AND still lined up with it — otherwise go around
  if (r && distToStrip(r, p.x, p.y) < 75 && Math.abs(angleDiff(p.ang, r.ang)) < LINEUP_TOL) {
    p.rollout = 0.0001; p.fade = 1;
  } else {
    p.landing = false; p.runway = null; w.stats.goArounds++;
    w.events.push({ t: 'goaround', id: p.id, x: p.x, y: p.y });
  }
}

/** Tap: toggle a holding pattern (drops any path). */
export function toggleHold(w: World, p: Aircraft): void {
  p.holding = !p.holding;
  p.idleSince = 0;
  w.stats.commands++;
  if (w.log) w.log.push({ tick: w.tick, t: 'hold', plane: p.id });
  if (p.holding) { p.path = []; p.pathSnapped = 0; p.landing = false; p.runway = null; p.pad = null; }
}

// ---------- re-exports for bots' direct deep-import of this file ----------
// bots/pilot.ts and bots/planner.ts import `World`, `SIM_DT`, `assignPath`,
// `toggleHold`, `needsAttention`, `airborneCount` from '../sim/world.ts'
// rather than the index, so those names must resolve here even though the
// functions themselves now live in route.ts / query.ts.
export { assignPath } from './route.ts';
export { needsAttention, airborneCount } from './query.ts';
